/**
 * Fair Home V2 — Capsules section (H4)
 * Figma `15:9530` desktop · `15:9551` compact
 */
import type { Audience } from '@/features/recommendations/api/recommendations'
import type {
  FairCapsuleCardV2Item,
  FairCapsuleCardV2ItemSlot,
  FairCapsuleCardV2Look,
} from '@/ui/tokens/capsule-card-v2.data'
import type { FairCapsuleItem } from '@/ui/tokens/fair-capsule.data'
import type { MainPageCapsulesResponse } from '@/features/capsules/api/capsules'
import type { CapsulesPersonalizationView } from '@/features/capsules/api/capsules.data'
import {
  mapCapsulesPreviewToFairSlides,
  type FairHomeCapsuleSlide,
} from '@/widgets/home/fair/fair-home-capsules.data'

export const FAIR_HOME_V2_CAPSULES_LIMIT = 10

/** Figma card widths — match FairCapsuleCardV2 tokens */
export const FAIR_HOME_V2_CAPSULES_CARD_WIDTH_DESKTOP = 560
export const FAIR_HOME_V2_CAPSULES_CARD_WIDTH_COMPACT = 350

export const FAIR_HOME_V2_CAPSULES_GAP_DESKTOP = 24
export const FAIR_HOME_V2_CAPSULES_GAP_COMPACT = 12

/** Look height of FairCapsuleCardV2 — the loading skeleton keeps the row's height. */
export const FAIR_HOME_V2_CAPSULES_LOOK_HEIGHT_DESKTOP = 480
export const FAIR_HOME_V2_CAPSULES_LOOK_HEIGHT_COMPACT = 418

export type FairHomeV2CapsulesLinkAction = 'auth' | 'taste'

export interface FairHomeV2CapsulesPersonaView {
  titleKey: string
  subtitleKey: string
  subtitleParams?: Record<string, number | string>
  linkKey?: string
  linkAction?: FairHomeV2CapsulesLinkAction
  showAiIcon: boolean
}

export interface FairHomeV2CapsuleSlide {
  id: number
  title: string
  description: string
  itemCount: number
  looks: FairCapsuleCardV2Look[]
}

/**
 * The section copy follows the Figma states (RedisignHomePage `15:9631` / `15:9652` / `15:9530`)
 * driven by the server's `personalization` block and nothing else:
 * a guest — "Capsules to explore · Sign in for capsules picked for you · Sign in";
 * a shopper without an applied profile — "Not tuned yet · Take the style quiz";
 * a tuned shopper — "Tuned to your style — N% match. · Improve" with the percent the taste term gave.
 */
export function resolveFairHomeV2CapsulesPersona(input: {
  isAuthenticated: boolean
  personalization: CapsulesPersonalizationView
}): FairHomeV2CapsulesPersonaView {
  const { isAuthenticated, personalization } = input

  if (!isAuthenticated) {
    return {
      titleKey: 'fair_home_v2.capsules.title_guest',
      subtitleKey: 'fair_home_v2.capsules.subtitle_guest',
      linkKey: 'fair_home_v2.capsules.link_sign_in',
      linkAction: 'auth',
      showAiIcon: true,
    }
  }

  if (personalization.tuned && personalization.matchPercent != null) {
    return {
      titleKey: 'fair_home_v2.capsules.title_default',
      subtitleKey: 'fair_home_v2.capsules.subtitle_partial',
      subtitleParams: { percent: personalization.matchPercent },
      linkKey: 'fair_home_v2.capsules.link_improve',
      linkAction: 'taste',
      showAiIcon: true,
    }
  }

  return {
    titleKey: 'fair_home_v2.capsules.title_default',
    subtitleKey: 'fair_home_v2.capsules.subtitle_newcomer',
    linkKey: 'fair_home_v2.capsules.link_style_quiz',
    linkAction: 'taste',
    showAiIcon: true,
  }
}

const HEADER_AUDIENCE_TO_API: Record<string, Audience> = {
  women: 'woman',
  woman: 'woman',
  men: 'man',
  man: 'man',
  kids: 'kids',
}

export function mapFairHomeV2CapsulesAudience(audience: string): Audience {
  return HEADER_AUDIENCE_TO_API[audience] ?? 'woman'
}

function mapFairCapsuleItemToV2(item: FairCapsuleItem): FairCapsuleCardV2Item {
  return {
    id: String(item.id),
    slot: item.type as FairCapsuleCardV2ItemSlot,
    imageUrl: item.imageUrl,
    alt: item.alt,
  }
}

function countCapsuleItems(looks: FairCapsuleCardV2Look[]): number {
  const ids = new Set<string>()
  for (const look of looks) {
    for (const item of look.items) {
      ids.add(item.id)
    }
  }
  return Math.max(1, ids.size)
}

function mapV1SlideToV2(slide: FairHomeCapsuleSlide): FairHomeV2CapsuleSlide {
  const looks: FairCapsuleCardV2Look[] = slide.looks.map((lookItems, index) => ({
    id: `${slide.id}-look-${index + 1}`,
    items: lookItems.map(mapFairCapsuleItemToV2),
  }))

  return {
    id: slide.id,
    title: slide.name,
    description: slide.description,
    itemCount: countCapsuleItems(looks),
    looks,
  }
}

export function resolveFairHomeV2CapsuleSlides(
  response: MainPageCapsulesResponse | null | undefined,
): FairHomeV2CapsuleSlide[] {
  return mapCapsulesPreviewToFairSlides(response).map(mapV1SlideToV2)
}

export function getFairHomeV2CapsulesCardWidth(compact: boolean): number {
  return compact
    ? FAIR_HOME_V2_CAPSULES_CARD_WIDTH_COMPACT
    : FAIR_HOME_V2_CAPSULES_CARD_WIDTH_DESKTOP
}

export function getFairHomeV2CapsulesLookHeight(compact: boolean): number {
  return compact
    ? FAIR_HOME_V2_CAPSULES_LOOK_HEIGHT_COMPACT
    : FAIR_HOME_V2_CAPSULES_LOOK_HEIGHT_DESKTOP
}

export function getFairHomeV2CapsulesGap(compact: boolean): number {
  return compact
    ? FAIR_HOME_V2_CAPSULES_GAP_COMPACT
    : FAIR_HOME_V2_CAPSULES_GAP_DESKTOP
}

export function getFairHomeV2CapsulesCardsPerView(
  compact: boolean,
  viewportWidth: number,
): number {
  if (compact) return 1
  const cardWidth = FAIR_HOME_V2_CAPSULES_CARD_WIDTH_DESKTOP
  const gap = FAIR_HOME_V2_CAPSULES_GAP_DESKTOP
  const step = cardWidth + gap
  if (step <= 0) return 1
  return Math.max(1, Math.floor((viewportWidth + gap) / step))
}

export function getFairHomeV2CapsulesPageCount(
  totalSlides: number,
  cardsPerView: number,
): number {
  if (totalSlides <= 0) return 1
  if (totalSlides <= cardsPerView) return 1
  return totalSlides - cardsPerView + 1
}

export function normalizeFairHomeV2CapsulesPage(
  page: number,
  pageCount: number,
): number {
  const safeTotal = Math.max(1, pageCount)
  return Math.min(Math.max(0, Math.floor(page)), safeTotal - 1)
}

export function resolveFairHomeV2CapsulesScrollPage(
  scrollLeft: number,
  step: number,
  pageCount: number,
): number {
  if (step <= 0) return 0
  return normalizeFairHomeV2CapsulesPage(Math.round(scrollLeft / step), pageCount)
}
